import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// LocalStorage keys
const STORAGE_KEYS = {
  CONVERSATIONS: 'garu_chat_conversations',
  ACTIVE_CONVERSATION_ID: 'garu_active_conversation_id',
};

// Data shape returned by the stylist function
type StyleData = {
  celebrity?: string;
  celebrityProfileImage?: string;
  outfit_description: string;
  key_pieces: string[];
  color_palette?: string[];
  styling_tips: string[];
  occasion?: string;
  celebrityImages?: string[];
  is_single_item?: boolean;
  item_category?: string;
  products?: Array<{
    piece: string;
    alternatives: Array<{
      title: string;
      imageUrl: string | null;
      price: string | null;
      link: string | null;
      source: string | null;
    }>;
  }>;
};

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  imageBase64?: string;
  data?: StyleData;
}

interface Conversation {
  id: string;
  messages: Message[];
  createdAt: Date;
}

interface MobileChatContextType {
  showMobileChatHistory: boolean;
  setShowMobileChatHistory: (show: boolean) => void;
  conversations: Conversation[];
  setConversations: (conversations: Conversation[] | ((prev: Conversation[]) => Conversation[])) => void;
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
}

const MobileChatContext = createContext<MobileChatContextType | undefined>(undefined);

export const useMobileChat = () => {
  const context = useContext(MobileChatContext);
  if (context === undefined) {
    throw new Error('useMobileChat must be used within a MobileChatProvider');
  }
  return context;
};

interface MobileChatProviderProps {
  children: ReactNode;
}

// Helper functions for localStorage persistence
const loadConversationsFromStorage = (): Conversation[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CONVERSATIONS);
    if (stored) {
      const parsed = JSON.parse(stored) as Array<Omit<Conversation, 'createdAt'> & { createdAt: string }>;
      // Convert createdAt strings back to Date objects
      return parsed.map((conv) => ({
        ...conv,
        createdAt: new Date(conv.createdAt),
      }));
    }
  } catch (error) {
    console.error('Error loading conversations from localStorage:', error);
  }
  return [];
};

const saveConversationsToStorage = (conversations: Conversation[]) => {
  try {
    localStorage.setItem(STORAGE_KEYS.CONVERSATIONS, JSON.stringify(conversations));
  } catch (error) {
    console.error('Error saving conversations to localStorage:', error);
  }
};

const loadActiveConversationIdFromStorage = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_CONVERSATION_ID);
  } catch (error) {
    console.error('Error loading active conversation ID from localStorage:', error);
    return null;
  }
};

const saveActiveConversationIdToStorage = (id: string | null) => {
  try {
    if (id) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_CONVERSATION_ID, id);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_CONVERSATION_ID);
    }
  } catch (error) {
    console.error('Error saving active conversation ID to localStorage:', error);
  }
};

export const MobileChatProvider = ({ children }: MobileChatProviderProps) => {
  const [showMobileChatHistory, setShowMobileChatHistory] = useState(false);
  
  const generateId = () => {
    try {
      // Prefer crypto.randomUUID when available, with safe access
      const maybeCrypto: unknown = typeof crypto !== 'undefined' ? crypto : undefined;
      const randomUUID = (maybeCrypto as { randomUUID?: () => string } | undefined)?.randomUUID;
      if (typeof randomUUID === 'function') {
        return randomUUID();
      }
    } catch (_) {
      // Ignore and fallback
    }
    return 'msg_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  };
  
  // Initialize state from localStorage
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    const loaded = loadConversationsFromStorage();
    // Backfill message ids for older stored conversations
    try {
      return loaded.map((conv) => ({
        ...conv,
        messages: conv.messages.map((m: Message) => ({
          id: m?.id || generateId(),
          role: m?.role,
          content: m?.content,
          imageBase64: m?.imageBase64,
          data: m?.data,
        })),
      }));
    } catch {
      return loaded;
    }
  });
  
  const [activeConversationId, setActiveConversationId] = useState<string | null>(() => {
    return loadActiveConversationIdFromStorage();
  });

  // Persist conversations to localStorage whenever they change
  useEffect(() => {
    saveConversationsToStorage(conversations);
  }, [conversations]);

  // Persist active conversation ID to localStorage whenever it changes
  useEffect(() => {
    saveActiveConversationIdToStorage(activeConversationId);
  }, [activeConversationId]);

  const setConversationsTyped = (conversations: Conversation[] | ((prev: Conversation[]) => Conversation[])) => {
    setConversations(conversations);
  };

  return (
    <MobileChatContext.Provider
      value={{
        showMobileChatHistory,
        setShowMobileChatHistory,
        conversations,
        setConversations: setConversationsTyped,
        activeConversationId,
        setActiveConversationId,
      }}
    >
      {children}
    </MobileChatContext.Provider>
  );
};
